import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateSocietyInput, CreateSocietyInputSchema } from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useCompaniesLookupQuery } from '@/features/companies/hooks/use-companies-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

/**
 * En edición las credenciales SAP son OPCIONALES: el API nunca las devuelve en
 * claro, así que el form arranca vacío y "vacío" significa "conservar las
 * actuales" (el panel las quita del payload antes del PATCH).
 */
const EditSocietyFormSchema = CreateSocietyInputSchema.extend({
  dbName: z.string().optional(),
  dbUser: z.string().optional(),
  dbPassword: z.string().optional()
})

export type SocietyFormValues = z.infer<typeof EditSocietyFormSchema>

type SocietyFormProps = {
  /** Alta (credenciales obligatorias) vs edición (vacías = conservar). */
  mode: 'create' | 'edit'
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateSocietyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: SocietyFormValues) => void
  onCancel: () => void
}

/**
 * Formulario de sociedad (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema (@idc-forms/schemas) que valida el backend; en edición
 * las credenciales se relajan a opcionales (ver EditSocietyFormSchema).
 */
export function SocietyForm({
  mode,
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: SocietyFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<SocietyFormValues>({
    resolver: zodResolver(mode === 'create' ? CreateSocietyInputSchema : EditSocietyFormSchema),
    defaultValues: {
      active: true,
      ...defaultValues
    }
  })

  const { data: companies = [] } = useCompaniesLookupQuery()

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="society-name">Nombre</FieldLabel>
                <Input
                  id="society-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Razón social completa, única entre sociedades.</FieldDescription>
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="company"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.company)}>
                <FieldLabel htmlFor="society-company">Empresa</FieldLabel>
                <NativeSelect
                  id="society-company"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una empresa
                  </NativeSelectOption>
                  {companies.map((company) => (
                    <NativeSelectOption key={company._id} value={company._id}>
                      {company.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.company]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Credenciales SAP</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="dbName"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.dbName)}>
                <FieldLabel htmlFor="society-db-name">Base de datos</FieldLabel>
                <Input
                  id="society-db-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                {mode === 'edit' && (
                  <FieldDescription>
                    Déjalo vacío para conservar la credencial actual.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.dbName]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="dbUser"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.dbUser)}>
                <FieldLabel htmlFor="society-db-user">Usuario</FieldLabel>
                <Input
                  id="society-db-user"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                {mode === 'edit' && (
                  <FieldDescription>
                    Déjalo vacío para conservar la credencial actual.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.dbUser]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="dbPassword"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.dbPassword)}>
                <FieldLabel htmlFor="society-db-password">Contraseña</FieldLabel>
                <Input
                  id="society-db-password"
                  type="password"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="new-password"
                />
                <FieldDescription>
                  {mode === 'edit'
                    ? 'Déjala vacía para conservar la actual. Se guarda encriptada.'
                    : 'Se guarda encriptada; nadie puede volver a verla en claro.'}
                </FieldDescription>
                <FieldError errors={[errors.dbPassword]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Estado</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="society-active">Activa</FieldLabel>
                <Switch
                  id="society-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
